import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useId, useRef, useState, } from 'react';
import { useMergeRefs } from '@floating-ui/react';
import { createPolymorphicComponent } from '../../helpers/react/polymorphicComponentTypes';
import { isFunction } from '../../helpers/isFunction';
import { useStyles } from '../../hooks/useStyles';
import { Divider } from '../Divider';
import { Base } from '../Base';
import { dialogContentStyles } from './DialogContent.styles';
import { dialogContentTheme } from './DialogContent.stylex';
// https://github.com/material-components/material-web/blob/main/dialog/internal/dialog.ts
export const DialogContent = createPolymorphicComponent(forwardRef(function DialogContent(props, forwardedRef) {
    const { styles, sx, type, scrollable, headline, icon, children, actions, onClose, ...other } = props;
    const headlineId = useId();
    const childrenId = useId();
    const { combineStyles, getStyles, globalStyles } = useStyles({
        name: 'DialogContent',
        styles: [dialogContentStyles, styles],
    });
    const [isAtScrollTop, setIsAtScrollTop] = useState(true);
    const [isAtScrollBottom, setIsAtScrollBottom] = useState(false);
    const showTopDivider = scrollable && !isAtScrollTop;
    const showBottomDivider = scrollable && !isAtScrollBottom;
    const scrollerRef = useRef(null);
    const topAnchorRef = useRef(null);
    const bottomAnchorRef = useRef(null);
    const handleAndhorIntersection = useCallback((entry) => {
        const { target, isIntersecting } = entry;
        if (target === topAnchorRef.current) {
            setIsAtScrollTop(isIntersecting);
        }
        if (target === bottomAnchorRef.current) {
            setIsAtScrollBottom(isIntersecting);
        }
    }, []);
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                handleAndhorIntersection(entry);
            }
        }, { root: scrollerRef.current });
        const topAnchorEl = topAnchorRef.current;
        if (topAnchorEl) {
            observer.observe(topAnchorRef.current);
        }
        const bottomAnchorEl = bottomAnchorRef.current;
        if (bottomAnchorEl) {
            observer.observe(bottomAnchorRef.current);
        }
        return () => {
            if (topAnchorEl) {
                observer.unobserve(topAnchorEl);
            }
            if (bottomAnchorEl) {
                observer.unobserve(bottomAnchorEl);
            }
        };
    }, [handleAndhorIntersection]);
    // TODO: Reset scroll position if re-opening a dialog with the same content.
    // See https://github.com/material-components/material-web/blob/main/dialog/internal/dialog.ts#L193C8-L193C75
    const nodeRef = useRef(null);
    const handleRef = useMergeRefs([nodeRef, forwardedRef]);
    return (_jsx(Base, { "aria-labelledby": headline ? headlineId : undefined, "aria-describedby": childrenId, role: type === 'alert' ? 'alertdialog' : undefined, ...other, sx: [dialogContentTheme, globalStyles, combineStyles('host'), sx], ref: handleRef, children: _jsx("div", { ...getStyles('dialog'), children: _jsxs("div", { ...getStyles('container'), children: [_jsxs("div", { ...getStyles('header'), children: [icon ? (_jsx("div", { ...getStyles('icon'), children: _jsx("div", { ...getStyles('iconSlot'), children: icon }) })) : null, _jsx("h2", { ...getStyles('headline'), id: headlineId, "aria-hidden": headline ? undefined : true, children: _jsx("div", { ...getStyles('headlineSlot', !!icon && 'headlineSlot$hasIcon', scrollable && 'headlineSlot$scrollable'), children: headline }) }), _jsx(Divider, { sx: combineStyles('divider', 'headlineDivider', showTopDivider && 'headlineDivider$showTopDivider') })] }), _jsx("div", { ref: scrollerRef, ...getStyles('scroller', scrollable && 'scroller$scrollable'), children: _jsx("div", { ...getStyles('content'), children: _jsxs("div", { ...getStyles('contentSlot', scrollable && 'contentSlot$scrollable', !!headline && 'contentSlot$hasHeadline', scrollable &&
                                    !!headline &&
                                    'contentSlot$scrollable$hasHeadline'), id: childrenId, children: [_jsx("div", { ref: topAnchorRef }), children, _jsx("div", { ref: bottomAnchorRef })] }) }) }), _jsxs("div", { ...getStyles('footer', !!actions && 'footer$hasActions'), children: [_jsx(Divider, { sx: combineStyles('divider', 'actionsDivider', showBottomDivider && 'actionsDivide$showBottomDivider') }), actions ? (_jsx("div", { ...getStyles('actions'), children: isFunction(actions)
                                    ? actions({ close: () => onClose?.() })
                                    : actions })) : null] })] }) }) }));
}));
